/**
 * KAN-883: состояние вида CRM "Лиды по дням": период в адресе (lib/crmStats,
 * по умолчанию 30 дней), менеджер из общих фильтров CRM, запрос
 * GET /api/v1/crm/stats/daily/ и переход из цифры в таблицу CRM.
 * Период выбирается одним полем CrmRangePicker.
 * KAN-910: быстрый период именем (?period=7d, setPreset), даты календаря ?from=&to= (setRange);
 * без периода в адресе пишется ?period=30d. Адрес - useUrlState.
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCrmLeadFilters, useDropRejectedCrmManager } from '@/hooks/useCrmLeadFilters';
import { useUrlDefaults, useUrlState } from '@/hooks/useUrlState';
import { CRM_STATS_DEFAULT_PERIOD, crmStatsDrillParams, parseCrmStatsPeriod, withCrmStatsPeriod } from '@/lib/crmStats';
import type { CrmRange } from '@/lib/crmTable';
import { CRM_PERIOD_PARAM } from '@/lib/crmTablePeriod';
import type { PeriodPreset } from '@/lib/periodPresets';
import { crmApi } from '@/services/api/crm';
import type { CrmDailyMetric, CrmDailyStatsParams } from '@/types/crm';

export const CRM_STATS_QUERY_KEY = ['crm', 'stats', 'daily'] as const;

export function useCrmStats() {
  const { params, update } = useUrlState();
  const leadFilters = useCrmLeadFilters();
  const from = params.get(CRM_PERIOD_PARAM.from);
  const to = params.get(CRM_PERIOD_PARAM.to);
  const named = params.get(CRM_PERIOD_PARAM.period);
  const period = useMemo(
    () =>
      parseCrmStatsPeriod(
        new URLSearchParams({
          [CRM_PERIOD_PARAM.from]: from ?? '',
          [CRM_PERIOD_PARAM.to]: to ?? '',
          [CRM_PERIOD_PARAM.period]: named ?? '',
        })
      ),
    [from, to, named]
  );
  const { range } = period;
  const manager = leadFilters.params.manager;

  useUrlDefaults([
    { key: CRM_PERIOD_PARAM.period, value: CRM_STATS_DEFAULT_PERIOD, unless: [CRM_PERIOD_PARAM.from, CRM_PERIOD_PARAM.to] },
  ]);

  const request: CrmDailyStatsParams = useMemo(() => {
    const value: CrmDailyStatsParams = { date_from: range.from, date_to: range.to };
    if (manager) value.manager = manager;
    return value;
  }, [range.from, range.to, manager]);

  const query = useQuery({
    queryKey: [...CRM_STATS_QUERY_KEY, request],
    queryFn: () => crmApi.getDailyStats(request),
  });
  useDropRejectedCrmManager(query.error, leadFilters);

  const setRange = useCallback((next: CrmRange) => update((prev) => withCrmStatsPeriod(prev, next)), [update]);
  const setPreset = useCallback((next: PeriodPreset) => update((prev) => withCrmStatsPeriod(prev, next)), [update]);

  /** Цифра дня (day) или итога (day null - весь период, быстрый пункт именем) открывает таблицу. */
  const drill = useCallback(
    (metric: CrmDailyMetric, day: string | null) =>
      update((prev) => crmStatsDrillParams(prev, metric, day ? { from: day, to: day } : (period.preset ?? range)), {
        push: true,
      }),
    [update, period.preset, range]
  );

  return { range, setRange, setPreset, leadFilters, query, drill };
}
